import { HeroUIProvider } from '@heroui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { Toaster } from 'react-hot-toast';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { theme } from './styles/theme';

// Context providers
import CookieConsentBanner from './components/CookieConsentBanner';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ConsentProvider } from './contexts/ConsentContext';
import { LocaleProvider } from './contexts/LocaleContext';

// Components
import Footer from './components/Footer';
import Header from './components/Header';
import SEOHead from './components/SEOHead';

// Pages
import About from './pages/About';
import Browse from './pages/Browse';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import CreatorDetail from './pages/CreatorDetail';
import GuestConfirmation from './pages/GuestConfirmation';
import GuestOrderTracking from './pages/GuestOrderTracking';
import Home from './pages/Home';
import Legal from './pages/Legal';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import ProductDetail from './pages/ProductDetail';
import Profile from './pages/Profile';
import Register from './pages/Register';
import Search from './pages/Search';
import TrackOrder from './pages/TrackOrder';

// Account pages
import AccountLayout from './pages/Account/AccountLayout';
import Dashboard from './pages/Account/Dashboard';
import Orders from './pages/Account/Orders';
import Security from './pages/Account/Security';
import Settings from './pages/Account/Settings';
import Tickets from './pages/Account/Tickets';

// Admin pages
import AdminLayout from './pages/Admin/AdminLayout';
import AdminAnalytics from './pages/Admin/Analytics';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminEmailSettings from './pages/Admin/EmailSettings';
import AdminOrderDetail from './pages/Admin/OrderDetail';
import OrderBoards from './pages/Admin/OrderBoards';
import AdminOrders from './pages/Admin/Orders';
import AdminSettings from './pages/Admin/Settings';
import AdminWarmup from './pages/Admin/Warmup';

// Auth pages
import ConfirmEmail from './pages/Auth/ConfirmEmail';
import ForgotPassword from './pages/Auth/ForgotPassword';
import OAuthCallback from './pages/Auth/OAuthCallback';
import ResetPassword from './pages/Auth/ResetPassword';

// Hooks
import { useAuth } from './contexts/AuthContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Authenticated route wrapper
interface AuthenticatedRouteProps {
  children: React.ReactNode;
}

const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600' />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/' replace />;
  }

  return <>{children}</>;
};

// Admin route wrapper
const AdminRoute: React.FC<AuthenticatedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600' />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  if (!isAdmin) {
    return <Navigate to='/' replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <HeroUIProvider locale='en-GB' skipFramerMotionAnimations={false}>
          <ConsentProvider>
            <AuthProvider>
              <LocaleProvider>
                <CartProvider>
                  <Router>
                    <div className='flex flex-col min-h-screen'>
                      <Header />
                      <main className='flex-1'>
                        <Routes>
                          <Route path='/' element={<Home />} />
                          <Route path='/search' element={<Search />} />
                          <Route path='/products' element={<Search />} />
                          <Route path='/browse' element={<Browse />} />
                          <Route
                            path='/products/:id/*'
                            element={<ProductDetail />}
                          />
                          <Route
                            path='/creator/:id'
                            element={<CreatorDetail />}
                          />
                          <Route path='/cart' element={<Cart />} />
                          <Route path='/login' element={<Login />} />
                          <Route path='/register' element={<Register />} />
                          <Route
                            path='/forgot-password'
                            element={<ForgotPassword />}
                          />
                          <Route
                            path='/reset-password'
                            element={<ResetPassword />}
                          />
                          <Route path='/track-order' element={<TrackOrder />} />
                          <Route path='/about' element={<About />} />
                          <Route path='/contact' element={<Contact />} />
                          <Route path='/legal' element={<Legal />} />
                          <Route path='/terms' element={<Legal />} />
                          <Route path='/privacy' element={<Legal />} />
                          <Route
                            path='/guest-order-tracking'
                            element={<GuestOrderTracking />}
                          />
                          <Route
                            path='/guest-confirmation'
                            element={<GuestConfirmation />}
                          />
                          <Route
                            path='/auth/callback/:provider'
                            element={<OAuthCallback />}
                          />
                          <Route path='/checkout' element={<Checkout />} />
                          <Route
                            path='/confirm-email'
                            element={<ConfirmEmail />}
                          />
                          <Route
                            path='/profile'
                            element={
                              <AuthenticatedRoute>
                                <Profile />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path='/account'
                            element={
                              <AuthenticatedRoute>
                                <AccountLayout />
                              </AuthenticatedRoute>
                            }
                          >
                            <Route
                              index
                              element={
                                <Navigate to='/account/dashboard' replace />
                              }
                            />
                            <Route path='dashboard' element={<Dashboard />} />
                            <Route path='profile' element={<Profile />} />
                            <Route path='orders' element={<Orders />} />
                            <Route path='tickets' element={<Tickets />} />
                            <Route path='security' element={<Security />} />
                            <Route path='settings' element={<Settings />} />
                          </Route>
                          <Route
                            path='/admin'
                            element={
                              <AdminRoute>
                                <AdminLayout />
                              </AdminRoute>
                            }
                          >
                            <Route index element={<AdminDashboard />} />
                            <Route path='orders' element={<AdminOrders />} />
                            <Route
                              path='orders/:orderId'
                              element={<AdminOrderDetail />}
                            />
                            <Route path='boards' element={<OrderBoards />} />
                            <Route
                              path='settings'
                              element={<AdminSettings />}
                            />
                            <Route path='warmup' element={<AdminWarmup />} />
                            <Route
                              path='email-settings'
                              element={<AdminEmailSettings />}
                            />
                            {/* Placeholder routes for missing components */}
                            <Route
                              path='analytics'
                              element={<AdminAnalytics />}
                            />
                            <Route
                              path='customers'
                              element={
                                <>
                                  <SEOHead
                                    title='Admin Customers'
                                    description='Manage customer accounts and profiles.'
                                  />
                                  <div className='p-6'>
                                    <h1 className='text-2xl font-bold'>
                                      Customers
                                    </h1>
                                    <p className='text-gray-600'>
                                      Coming soon...
                                    </p>
                                  </div>
                                </>
                              }
                            />
                          </Route>
                          <Route path='*' element={<NotFound />} />
                        </Routes>
                      </main>
                      <Footer />
                      <CookieConsentBanner />
                    </div>
                  </Router>
                </CartProvider>
              </LocaleProvider>
            </AuthProvider>
          </ConsentProvider>
          <Toaster position='top-right' />
        </HeroUIProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
